import { NavLink as ReactRouterNavLink } from 'react-router-dom'
import styled, { css, type DefaultTheme, RuleSet } from 'styled-components'

import { hiddenScrollbar, media } from '@/theme/common'

import { NavbarMenuMode, NavSizeKeys } from './navbar-menu.types'

export const ScrollContainer = styled.div`
  align-items: center;
  display: flex;
`

export const ScrollableNav = styled.nav`
  flex: 1;
  overflow: hidden;
`

export const ScrollableNavList = styled.ul<{ $mode?: NavbarMenuMode }>`
  display: flex;
  flex-direction: row;

  ${({ $mode }) =>
    $mode === 'tabs'
      ? css`
          gap: 0.8rem;
          width: 100%;
          > li {
            display: flex;
            flex: 1 0 0;
          }
        `
      : css`
          ${hiddenScrollbar};
          overflow-x: auto;
        `}
`

const navSizeStyles = (theme: DefaultTheme, $size: NavSizeKeys) => {
  // Note: roughly-halved horizontal padding on mobile - same "tighter mobile spacing" pattern
  // as ui/table's cell padding and TableGroup's Card padding, so nav labels don't eat into the
  // scarce width a narrow viewport already has to share with everything else in the toolbar.
  const sizeStyles: Record<NavSizeKeys, RuleSet> = {
    large: css`
      font-size: 1.6rem;
      line-height: 2.4rem;
      padding: ${theme.space['12']} ${theme.space['24']};

      ${media.down('sm')} {
        padding: ${theme.space['12']} ${theme.space['12']};
      }

      > svg {
        width: 2.4rem;
      }
    `,
    medium: css`
      font-size: 1.4rem;
      line-height: 2rem;
      padding: ${theme.space['10']} ${theme.space['20']};

      ${media.down('sm')} {
        padding: ${theme.space['10']} ${theme.space['10']};
      }

      > svg {
        width: 2rem;
      }
    `,
    small: css`
      font-size: 1.2rem;
      line-height: 1.6rem;
      padding: ${theme.space['8']} ${theme.space['16']};

      ${media.down('sm')} {
        padding: ${theme.space['8']} ${theme.space['8']};
      }

      > svg {
        width: 1.6rem;
      }
    `,
  }

  return sizeStyles[$size]
}

const navLinkStyles = (theme: DefaultTheme, $size: NavSizeKeys) => css`
  display: flex;
  gap: ${theme.space['4']};
  font-weight: 500;
  text-wrap: nowrap;
  color: ${theme.colors.primary600};
  border-bottom: ${theme.border.size['2']} solid transparent;

  > svg {
    aspect-ratio: 1 / 1;
  }

  &:hover {
    color: ${theme.colors.primary800};
    border-bottom-color: ${theme.colors.primary600};
  }

  &:focus-visible {
    border-bottom-color: ${theme.colors.primary600};
    background: ${theme.colors.primary100};
    color: ${theme.colors.primary600};
  }

  &:active,
  &.active {
    border-bottom-color: ${theme.colors.primary800};
    color: ${theme.colors.primary800};
  }

  ${navSizeStyles(theme, $size)}
`

const navButtonStyles = (theme: DefaultTheme, $size: NavSizeKeys) => css`
  background: transparent;
  border: none;
  cursor: pointer;
  display: flex;
  gap: ${theme.space['4']};
  font-weight: 500;
  text-wrap: nowrap;
  color: ${theme.colors.primary600};
  border-bottom: ${theme.border.size['2']} solid transparent;

  > svg {
    aspect-ratio: 1 / 1;
  }

  &:hover {
    color: ${theme.colors.primary800};
    border-bottom-color: ${theme.colors.primary600};
  }

  &:focus-visible {
    border-bottom-color: ${theme.colors.primary600};
    background: ${theme.colors.primary100};
    color: ${theme.colors.primary600};
  }

  &:active,
  &[data-active='true'] {
    border-bottom-color: ${theme.colors.primary800};
    color: ${theme.colors.primary800};
  }

  &:disabled {
    color: ${theme.colors.gray800};
  }

  ${navSizeStyles(theme, $size)}
`

export const NavLink = styled(ReactRouterNavLink)<{ $size: NavSizeKeys }>(({ theme, $size }) =>
  navLinkStyles(theme, $size),
)

export const NavButton = styled.button<{ $size: NavSizeKeys }>(({ theme, $size }) =>
  navButtonStyles(theme, $size),
)

export const TabButton = styled.button<{ $active?: boolean }>(({ theme, $active }) => ({
  alignItems: 'center',
  background: $active ? theme.colors.primary100 : theme.colors.white,
  border: `${theme.border.size['1']} solid ${$active ? theme.colors.black800 : theme.colors.gray400}`,
  borderRadius: '0.6rem',
  color: theme.colors.primary800,
  cursor: 'pointer',
  display: 'flex',
  flex: '1 0 0',
  flexDirection: 'column',
  fontSize: '1.4rem',
  fontWeight: 500,
  gap: theme.space['8'],
  lineHeight: '2rem',
  padding: theme.space['12'],
  width: '100%',
}))
